import React, { Fragment } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import DimpleDivider from '@tds/core-dimple-divider';

import FormSummaryRow from './formSummaryRow';
import NoInformationFound from '../design/noInformationFound';
import BusinessPlansTable from './businessPlansTable';

import { GroupWrapper, GroupTitle } from './css';
import {
  smbIndirectSalesChannels,
  keyDirectoryListingDetails,
  keyChannelId,
  keyOutlet,
  keyChannelName,
  fieldBAN,
  fieldExistingBusinessConnectBANNumber,
  fieldAvailableDiscountsAndPromotions,
  fieldCoperToFibreMigration,
  fieldC2fAutomatedActivation,
  fieldExistingCustomerBA,
  fieldExistingCustomerDomain
} from '../utils/constant';
import FormField from './formField';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import { isSalesSupport } from '../utils/request';
import SummaryRatePlan from '../iot/viewOrderSummary/summaryRatePlan';
import SummaryRatePlanRenewal from '../iot/viewOrderSummary/summaryRatePlanRenewal';
import SummaryAddons from '../iot/viewOrderSummary/summaryAddons';
import SummaryAdditionalServices from '../iot/viewOrderSummary/summaryAdditionalServices';
import SummaryHardware from '../iot/viewOrderSummary/summaryHardware';
import SummaryVehicleInfo from '../iot/viewOrderSummary/summaryVehicleInfo';
import SummaryTrackerSummary from '../iot/viewOrderSummary/summaryTrackerSummary';

const FormSummaryGroup = ({ data, orderData: backendObject }) => {
  const localizedString = useLocalizedStringsContext();
  const {
    contentfulValues: { yes, no },
    productInOrderForOptions: { googleWorkspace, microsoft365 },
    existingCustomerBA_QuestionLabels: {
      googleWorkspaceCustomer,
      googleWorkspaceDomain,
      microsoft365Customer,
      microsoft365Domain
    }
  } = localizedString;
  const isFieldVisible = backendFieldValidation => {
    let isVisible = true;
    if (backendFieldValidation) {
      for (let i = 0; i < backendFieldValidation.length; i += 1) {
        const {
          fields: { key: fieldKey, value }
        } = backendFieldValidation[i];
        isVisible =
          isVisible &&
          (value.split('|').includes(backendObject[fieldKey]) ||
            (value.split('|').includes('undefined') &&
              typeof backendObject[fieldKey] === 'undefined'));
      }
    }
    return isVisible;
  };

  const getInternetPlan = speed => {
    const localizedStrings = useLocalizedStringsContext();
    const {
      summaryForm: { plans }
    } = localizedStrings;

    return _.get(plans, speed, '');
  };

  const getFieldValue = (fieldKey, fieldValue) => {
    if (!fieldValue) return false;
    if (fieldKey === fieldAvailableDiscountsAndPromotions)
      return fieldValue.join(' + ');
    return [
      'currentInternetPlan',
      'plan',
      'whatSpeedAreTheyUpgradingTo'
    ].includes(fieldKey)
      ? getInternetPlan(fieldValue)
      : fieldValue;
  };

  const getUpdateLabel = (fieldName, fieldLabel) => {
    let updatedLabel = fieldLabel;
    if (
      backendObject.productInOrderFor === googleWorkspace &&
      fieldName === fieldExistingCustomerBA
    ) {
      updatedLabel = googleWorkspaceCustomer;
    }
    if (
      backendObject.productInOrderFor === microsoft365 &&
      fieldName === fieldExistingCustomerBA
    ) {
      updatedLabel = microsoft365Customer;
    }
    if (
      backendObject.productInOrderFor === googleWorkspace &&
      fieldName === fieldExistingCustomerDomain
    ) {
      updatedLabel = googleWorkspaceDomain;
    }
    if (
      backendObject.productInOrderFor === microsoft365 &&
      fieldName === fieldExistingCustomerDomain
    ) {
      updatedLabel = microsoft365Domain;
    }
    return updatedLabel;
  };

  const {
    fields: { backendFields, backendKey, name, backendFieldValidation }
  } = data;

  if (!isFieldVisible(backendFieldValidation)) return null;

  const fieldData = backendFields.map((backendField, index) => {
    const {
      sys: {
        contentType: {
          sys: { id }
        },
        id: sysId
      },
      fields: { key, backendFieldValidation: subBackendFieldValidation }
    } = backendField;
    const keyId = `summary-${backendKey}${index}${sysId}${name}`;

    if (!isFieldVisible(subBackendFieldValidation)) return null;
    if (key === 'iotSummaryRatePlan') {
      return (
        <div style={{ margin: '1rem 0' }}>
          <SummaryRatePlan
            orderData={backendObject}
            total={_.get(backendObject, 'total.ratePlan', 0)}
          />
          <DimpleDivider />
        </div>
      );
    }
    if (key === 'iotSummaryRatePlanRenewal') {
      return (
        <div style={{ margin: '1rem 0' }}>
          <SummaryRatePlanRenewal
            renewalSubscriberList={_.get(
              backendObject,
              'renewalSubscriberList',
              []
            )}
          />
          <DimpleDivider />
        </div>
      );
    }
    if (key === 'iotSummaryAddons') {
      return (
        <div style={{ margin: '1rem 0' }}>
          <SummaryAddons
            addons={_.get(backendObject, 'addons', {})}
            total={_.get(backendObject, 'total.addons', 0)}
          />
          <DimpleDivider />
        </div>
      );
    }
    if (key === 'iotAdditionalServices') {
      return (
        <div style={{ margin: '1rem 0' }}>
          <SummaryAdditionalServices
            additionalServices={_.get(backendObject, 'additionalServices', {})}
            total={_.get(backendObject, 'total.additionalServices', 0)}
          />
          <DimpleDivider />
        </div>
      );
    }
    if (key === 'iotSummaryHardware') {
      return (
        <div style={{ margin: '1rem 0' }}>
          <SummaryHardware
            orderData={backendObject}
            total={_.get(backendObject, 'total.hardware', 0)}
          />
          <DimpleDivider />
        </div>
      );
    }
    if (key === 'iotSummaryVehicleInfo') {
      return (
        <div style={{ margin: '1rem 0' }}>
          <SummaryVehicleInfo
            vehicleInfo={_.get(backendObject, 'vehicleInfo', [])}
          />
          <DimpleDivider />
        </div>
      );
    }
    if (key === 'iotSummaryTracker') {
      return (
        <div style={{ margin: '1rem 0' }}>
          <SummaryTrackerSummary orderData={backendObject} />
        </div>
      );
    }
    if (
      key === 'businessAppPlans' &&
      backendObject.businessAppPlans &&
      backendObject.businessAppPlans.length > 0
    ) {
      return (
        <div style={{ margin: '1rem 0' }}>
          <BusinessPlansTable
            businessAppPlans={backendObject.businessAppPlans}
            onDelete={() => {}}
          />
        </div>
      );
    }
    if (id === 'formSummaryStepGroup') {
      return (
        <GroupWrapper style={{ marginBottom: '1em' }} key={keyId}>
          <GroupTitle style={{ marginBottom: '1em' }}>
            {backendField.fields.name}
          </GroupTitle>
          <FormSummaryGroup data={backendField} orderData={backendObject} />
        </GroupWrapper>
      );
    }
    if (id === 'repeatedSummaryGroup') {
      const keyValue = backendObject[backendField.fields.key];
      const currentFieldName = backendField.fields.name;
      const subObjectList = (Array.isArray(keyValue) && keyValue) || [];
      if (!subObjectList.length) return null;
      return (
        <GroupWrapper style={{ marginBottom: '1em' }}>
          {subObjectList.map((item, i) => {
            let subSecKey =
              subObjectList.length > 1
                ? `${currentFieldName} ${i + 1}`
                : currentFieldName;
            if (backendField.fields.key === keyDirectoryListingDetails) {
              subSecKey = item.title;
            }
            return (
              <Fragment key={subSecKey}>
                <GroupTitle style={{ marginBottom: '1em' }}>
                  {subSecKey}
                </GroupTitle>
                <FormSummaryGroup data={backendField} orderData={item} />
              </Fragment>
            );
          })}
        </GroupWrapper>
      );
    }
    const { key: fieldKey, value: fieldValue } = backendField.fields;
    let { label } = backendField.fields;
    const value = backendObject[fieldKey];
    const {
      role,
      channels,
      handleFormField,
      error,
      ref,
      isSelectAllowed
    } = backendObject;

    if (
      fieldKey === 'email_administratorContact' &&
      _.get(backendObject, fieldC2fAutomatedActivation, '') === yes
    ) {
      return (
        <>
          <FormSummaryRow
            key={fieldKey}
            label={label}
            value="rpabot.businessconnectvoicesetup@telus.com"
          />
          <FormSummaryRow
            key="customerEmail_administratorContact"
            label="Customer Address Address"
            value={value}
          />
        </>
      );
    }

    if (
      [fieldExistingCustomerBA, fieldExistingCustomerDomain].includes(fieldKey)
    ) {
      label = getUpdateLabel(fieldKey, label);
    }

    if (
      fieldKey === fieldExistingBusinessConnectBANNumber &&
      backendObject[fieldBAN]
    ) {
      return null;
    }
    const channelName = _.get(channels, '0.description', '');
    if (
      _.get(role, 'id', 0) === 2 &&
      Object.keys(smbIndirectSalesChannels).includes(channelName) &&
      fieldKey === keyOutlet
    ) {
      const selectData = {
        label,
        ref,
        type: 'Select',
        name: fieldKey,
        required: true,
        value: backendObject[fieldKey],
        options: Object.values(smbIndirectSalesChannels).map(channel => {
          return { fields: { label: channel[0], value: channel[0] } };
        }),
        onChange: e => handleFormField(e.target, true)
      };
      return <FormField data={selectData} error={error} />;
    }

    /** For role sales support show a channel dropdown to select from
     * before they start creating orders
     */
    if (isSalesSupport && fieldKey === keyChannelName && isSelectAllowed) {
      if (Array.isArray(channels) && channels.length > 1) {
        const options = channels.map(item => ({
          fields: { value: item.id, label: item.name }
        }));
        const selectData = {
          label,
          ref,
          type: 'Select',
          name: keyChannelId,
          required: true,
          value: '',
          options: [...options],
          onChange: e => handleFormField(e.target, true)
        };

        return <FormField data={selectData} error={error} />;
      }
    }

    /**
     * If coper to fibre migration is No
     * then hide the row from the order summary
     */
    if (
      fieldKey === fieldCoperToFibreMigration &&
      getFieldValue(fieldKey, value) === no
    )
      return null;

    /**
     * For Salessupport role if on prequalification screen
     * only show the channel field and hide rest of them
     * while on order summary screen show all fields
     */
    if (isSalesSupport && fieldKey !== keyChannelName && isSelectAllowed) {
      return null;
    }

    return (
      <FormSummaryRow
        key={key}
        label={label}
        value={
          typeof value === 'boolean'
            ? fieldValue
            : getFieldValue(fieldKey, value)
        }
      />
    );
  });

  let isSectionNull = true;
  fieldData.forEach(item => {
    if (item != null && _.get(item, 'props.value', true)) isSectionNull = false;
  });

  if (isSectionNull) return <NoInformationFound />;
  return fieldData;
};

FormSummaryGroup.propTypes = {
  data: PropTypes.shape({
    backendKey: PropTypes.string,
    backendFields: PropTypes.arrayOf(PropTypes.object)
  }).isRequired,
  orderData: PropTypes.objectOf(PropTypes.object).isRequired
};

export default FormSummaryGroup;
